import { useEffect, useState } from 'react'
import { useQueryState } from 'nuqs'
import { useRecommendations } from '@/features/CrossSell/hooks/useRecommendations'
import type { PriceIntentFragment } from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { priceIntentServiceInitClientSide } from '@/services/priceIntent/PriceIntentService'
import { useShopSessionId } from '@/services/shopSession/ShopSessionContext'
import { useApolloClient } from '@apollo/client'

export const PREFILL_FROM_RECOMMENDATION_QUERY_PARAM = 'prefillFromRecommendation'

/**
 * Seeds a fresh price intent with Insurely-derived prefill data when the customer arrives from a
 * product-only recommendation (RecommendedProduct -> "Get price"). It never confirms (so the
 * customer lands on the prefilled form) and never clobbers an intent that already has data or offers.
 */
export function usePrefillFromRecommendation(priceIntent?: PriceIntentFragment) {
  const [prefillParam, setPrefillParam] = useQueryState(PREFILL_FROM_RECOMMENDATION_QUERY_PARAM)
  const apolloClient = useApolloClient()
  const shopSessionId = useShopSessionId()
  const { productRecommendations, loading: isRecommendationsLoading } = useRecommendations()
  const [isPrefilled, setIsPrefilled] = useState(false)

  const shouldPrefill = prefillParam != null

  useEffect(() => {
    if (!shouldPrefill || !priceIntent || !shopSessionId || isPrefilled || isRecommendationsLoading) return

    const apply = async () => {
      const prefillData = productRecommendations.find(
        (rec) => rec.product.name === priceIntent.product.name,
      )?.priceIntentData

      const isFreshIntent = priceIntent.offers.length === 0 && Object.keys(priceIntent.data).length === 0

      if (prefillData != null && isFreshIntent) {
        try {
          const service = priceIntentServiceInitClientSide(apolloClient, logger)
          await service.update({ priceIntentId: priceIntent.id, data: prefillData, customer: { shopSessionId } })
        } catch (error) {
          logger.warn('Failed to prefill price intent from recommendation', { error })
        }
      }

      setIsPrefilled(true)
      // Strip the param so a refresh or back navigation doesn't re-trigger the prefill.
      setPrefillParam(null)
    }

    apply()
  }, [
    shouldPrefill,
    priceIntent,
    shopSessionId,
    isPrefilled,
    isRecommendationsLoading,
    productRecommendations,
    apolloClient,
    setPrefillParam,
  ])

  // Should prefill but is not yet prefilled
  return { isPrefilling: shouldPrefill && !isPrefilled }
}
